import { useState } from "react";
import { Tag } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import type { Database } from "@/integrations/supabase/types";

type TimeCategory = Database["public"]["Enums"]["time_category"];

export interface CategoryOption {
  id: TimeCategory | "custom";
  name: string;
  color: string;
}

export const defaultCategories: CategoryOption[] = [
  { id: "dev", name: "Desenvolvimento", color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300" },
  { id: "meeting", name: "Reunião", color: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300" },
  { id: "research", name: "Pesquisa", color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300" },
  { id: "admin", name: "Administrativo", color: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300" },
  { id: "custom", name: "Personalizada", color: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300" },
];

interface CategorySelectProps {
  value: TimeCategory;
  customCategory: string;
  onCategoryChange: (category: TimeCategory) => void;
  onCustomCategoryChange: (customCategory: string) => void;
  disabled?: boolean;
}

export function CategorySelect({
  value,
  customCategory,
  onCategoryChange,
  onCustomCategoryChange,
  disabled = false,
}: CategorySelectProps) {
  const [showCustomInput, setShowCustomInput] = useState(!!customCategory);

  const handleValueChange = (newValue: string) => {
    if (newValue === "custom") {
      setShowCustomInput(true);
      // Keep the current enum value but enable custom input
    } else {
      setShowCustomInput(false);
      onCustomCategoryChange("");
      onCategoryChange(newValue as TimeCategory);
    }
  };

  const displayValue = showCustomInput ? "custom" : value;

  return (
    <div className="space-y-2">
      <Select
        value={displayValue}
        onValueChange={handleValueChange}
        disabled={disabled}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Selecione" />
        </SelectTrigger>
        <SelectContent>
          {defaultCategories.map((category) => (
            <SelectItem key={category.id} value={category.id}>
              <span className="flex items-center gap-2">
                {category.id === "custom" && <Tag className="h-3 w-3" />}
                {category.name}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {showCustomInput && (
        <Input
          value={customCategory}
          onChange={(e) => onCustomCategoryChange(e.target.value.slice(0, 50))}
          placeholder="Nome da categoria (máx 50)"
          maxLength={50}
          disabled={disabled}
          className="text-sm"
        />
      )}

      {showCustomInput && customCategory && (
        <p className="text-xs text-muted-foreground">
          {customCategory.length}/50 caracteres
        </p>
      )}
    </div>
  );
}

export function getCategoryDisplay(
  category: TimeCategory | null,
  customCategory: string | null
): { name: string; color: string } {
  if (customCategory) {
    return {
      name: customCategory,
      color: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300",
    };
  }

  const found = defaultCategories.find((c) => c.id === category);
  return found || { name: category || "-", color: "" };
}
